import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { useControllable } from '../../hooks/useControllable';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import styles from './Switch.module.css';

type SwitchSize = 'sm' | 'md';

interface SwitchProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled checked state */
  checked?: boolean;
  /** Initial checked state (uncontrolled) */
  defaultChecked?: boolean;
  /** Called when value changes */
  onChange?: (checked: boolean) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Show loading spinner */
  loading?: boolean;
  /** Switch size */
  size?: SwitchSize;
  /** Content inside when checked */
  checkedChildren?: React.ReactNode;
  /** Content inside when unchecked */
  unCheckedChildren?: React.ReactNode;
}

// ── CVA definitions ──────────────────────────────────────────────────────────

const track = cva(styles.track, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
    },
    checked: {
      true: styles.checked,
    },
    disabled: {
      true: styles.trackDisabled,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const thumb = cva(styles.thumb, {
  variants: {
    size: {
      sm: styles.thumbSm,
      md: styles.thumbMd,
    },
    checked: {
      true: styles.thumbChecked,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const childLabel = cva(styles.childLabel, {
  variants: {
    size: {
      sm: styles.childLabelSm,
      md: styles.childLabelMd,
    },
    side: {
      left: styles.childLabelLeft,
      right: styles.childLabelRight,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const spinner = cva(styles.spinner, {
  variants: {
    size: {
      sm: styles.spinnerSm,
      md: styles.spinnerMd,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// Runtime translate values per size (for Framer Motion animate)
const translateValues = {
  sm: 14,
  md: 20,
};

// Runtime child label offset values per size
const childLabelOffset = {
  sm: 4, // padding(2) + 2
  md: 4,
};

/**
 * Switch — toggle between on/off states.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | checked | boolean | — | Controlled state |
 * | defaultChecked | boolean | false | Initial state (uncontrolled) |
 * | onChange | (checked) => void | — | Value change handler |
 * | disabled | boolean | false | Disable interaction |
 * | loading | boolean | false | Show loading spinner on thumb |
 * | size | 'sm' \| 'md' | 'md' | Switch size |
 * | checkedChildren | ReactNode | — | Label when on |
 * | unCheckedChildren | ReactNode | — | Label when off |
 *
 * @example
 * <Switch />
 * <Switch defaultChecked onChange={(v) => console.log(v)} />
 * <Switch checked={isDark} onChange={toggleTheme} checkedChildren="On" unCheckedChildren="Off" />
 * <Switch loading />
 */
export function Switch({
  checked: controlledChecked,
  defaultChecked = false,
  onChange,
  disabled = false,
  loading = false,
  size = 'md',
  checkedChildren,
  unCheckedChildren,
  className,
  style,
  ...props
}: SwitchProps) {
  const [checked, setChecked] = useControllable(controlledChecked, defaultChecked, onChange);
  const isDisabled = disabled || loading;
  const translate = translateValues[size];
  const offset = childLabelOffset[size];
  const shouldReduce = useReducedMotion();

  const handleClick = () => {
    if (isDisabled) return;
    setChecked(!checked);
  };

  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleClick(); }
      }}
      className={clsx(
        track({ size, checked: checked || undefined, disabled: isDisabled || undefined }),
        className,
      )}
      style={style}
      {...props}
    >
      {checkedChildren && checked && (
        <span
          className={childLabel({ size, side: 'left' })}
          style={{ '--switch-label-offset': `${offset}px` } as React.CSSProperties}
        >
          {checkedChildren}
        </span>
      )}
      {unCheckedChildren && !checked && (
        <span
          className={childLabel({ size, side: 'right' })}
          style={{ '--switch-label-offset': `${offset}px` } as React.CSSProperties}
        >
          {unCheckedChildren}
        </span>
      )}
      <motion.div
        animate={{ x: checked ? translate : 0 }}
        transition={shouldReduce ? { duration: 0 } : { type: 'spring', stiffness: 600, damping: 32 }}
        className={thumb({ size, checked: checked || undefined })}
      >
        {loading && (
          <span className={spinner({ size })} />
        )}
      </motion.div>
    </div>
  );
}
